
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";

import { useTranslation } from "react-i18next";

import {
  FaGlobe,
  FaFacebookF,
  FaInstagram,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaPlus,
  FaApple,
  FaGooglePlay,
  FaExternalLinkAlt,
} from "react-icons/fa";

import { createClient } from "@supabase/supabase-js";

import "./ProjectsPage.css";

// ============================================================
// SUPABASE
// ============================================================

const SUPABASE_URL =
  "https://xrxkmksbutmuouddtzcm.supabase.co";

const SUPABASE_ANON_KEY =
  "sb_publishable_bQxGcshN4etGOcOi2lVWFA_tBESheVk";

const supabase = createClient(
  SUPABASE_URL,
  SUPABASE_ANON_KEY
);

// ============================================================
// DATABASE
// ============================================================

const PROJECTS_TABLE = "projects";

// ============================================================
// STORAGE
// ============================================================

const PROJECTS_BUCKET = "projects-images";

// ============================================================
// IMAGE EXTENSIONS
// ============================================================

const IMAGE_EXTENSIONS = [
  "jpg",
  "jpeg",
  "png",
  "webp",
  "gif",
];

// ============================================================
// CATEGORY FALLBACK
// ============================================================

const formatCategoryFallback = (category) => {
  if (!category) {
    return "";
  }

  return category
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) =>
      char.toUpperCase()
    );
};

// ============================================================
// CATEGORY ORDER
// ============================================================

const CATEGORY_ORDER = [
  "website",
  "app",
  "neural_network",
  "dock",
];

// ============================================================
// PROJECTS PAGE
// ============================================================

const ProjectsPage = () => {
  const { t, i18n } = useTranslation();

  // ==========================================================
  // MODAL
  // ==========================================================

  const [selectedProject, setSelectedProject] =
    useState(null);

  const [imageIndex, setImageIndex] = useState(0);

  // ==========================================================
  // PROJECTS
  // ==========================================================

  const [projects, setProjects] = useState([]);

  // ==========================================================
  // LOADING
  // ==========================================================

  const [loading, setLoading] = useState(true);

  // ==========================================================
  // ERROR
  // ==========================================================

  const [error, setError] = useState(null);

  // ==========================================================
  // AMBIENT GLOW
  // ==========================================================

  const containerRef = useRef(null);

  const handleMouseMove = useCallback((e) => {
    const el = containerRef.current;

    if (!el) {
      return;
    }

    const rect =
      el.getBoundingClientRect();

    el.style.setProperty(
      "--glow-x",
      `${e.clientX - rect.left}px`
    );

    el.style.setProperty(
      "--glow-y",
      `${e.clientY - rect.top}px`
    );
  }, []);

  // ==========================================================
  // LANGUAGE
  // ==========================================================

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);

    document.dir =
      lang === "ar" ? "rtl" : "ltr";
  };

  // ==========================================================
  // PROJECT NAME
  // ==========================================================

  const getProjectName = useCallback(
    (project) => {
      if (i18n.language === "ar") {
        return (
          project.name_ar ||
          project.name_en ||
          project.folder_name
        );
      }

      return (
        project.name_en ||
        project.name_ar ||
        project.folder_name
      );
    },
    [i18n.language]
  );

  // ==========================================================
  // PROJECT DESCRIPTION
  // ==========================================================

  const getProjectDescription = useCallback(
    (project) => {
      if (i18n.language === "ar") {
        return (
          project.description_ar ||
          project.description_en ||
          ""
        );
      }

      return (
        project.description_en ||
        project.description_ar ||
        ""
      );
    },
    [i18n.language]
  );

  // ==========================================================
  // CATEGORY TITLE
  // ==========================================================

  const getCategoryTitle = useCallback(
    (category) => {
      if (!category) {
        return "";
      }

      const translated = t(category, {
        defaultValue: "",
      });

      if (translated) {
        return translated;
      }

      return formatCategoryFallback(category);
    },
    [t]
  );

  // ==========================================================
  // FETCH PROJECTS
  // ==========================================================

  useEffect(() => {
    let isMounted = true;

    const fetchProjects = async () => {
      try {
        setLoading(true);
        setError(null);

        const {
          data: projectRows,
          error: databaseError,
        } = await supabase
          .from(PROJECTS_TABLE)
          .select(`
            id,
            folder_name,
            category,
            name_en,
            name_ar,
            description_en,
            description_ar,
            apple_link,
            google_link
          `)
          .order("id", {
            ascending: true,
          });

        if (!isMounted) return;

        if (databaseError) {
          throw databaseError;
        }

        if (
          !projectRows ||
          projectRows.length === 0
        ) {
          setProjects([]);
          return;
        }

        const projectsWithImages =
          await Promise.all(
            projectRows.map(
              async (project) => {
                const rawFolder =
                  project.folder_name;

                if (
                  !rawFolder ||
                  rawFolder.trim() === ""
                ) {
                  return {
                    ...project,
                    images: [],
                  };
                }

                const folderName =
                  rawFolder
                    .trim()
                    .replace(
                      /^\/+|\/+$/g,
                      ""
                    );

                const {
                  data: files,
                  error: filesError,
                } =
                  await supabase.storage
                    .from(
                      PROJECTS_BUCKET
                    )
                    .list(
                      folderName,
                      {
                        limit: 100,
                        offset: 0,
                        sortBy: {
                          column: "name",
                          order: "asc",
                        },
                      }
                    );

                if (filesError) {
                  console.error(
                    `Storage error for "${folderName}":`,
                    filesError.message
                  );

                  return {
                    ...project,
                    images: [],
                  };
                }

                if (
                  !files ||
                  files.length === 0
                ) {
                  return {
                    ...project,
                    images: [],
                  };
                }

                const imageFiles =
                  files.filter(
                    (file) => {
                      if (!file?.name) {
                        return false;
                      }

                      if (
                        file.name ===
                          ".emptyFolderPlaceholder" ||
                        file.name.startsWith(
                          "."
                        )
                      ) {
                        return false;
                      }

                      const extension =
                        file.name
                          .split(".")
                          .pop()
                          ?.toLowerCase();

                      return IMAGE_EXTENSIONS.includes(
                        extension
                      );
                    }
                  );

                const images =
                  imageFiles.map(
                    (file) => {
                      const filePath =
                        `${folderName}/${file.name}`;

                      const { data } =
                        supabase.storage
                          .from(
                            PROJECTS_BUCKET
                          )
                          .getPublicUrl(
                            filePath
                          );

                      return data.publicUrl;
                    }
                  );

                return {
                  ...project,
                  images,
                };
              }
            )
          );

        if (!isMounted) return;

        setProjects(
          projectsWithImages
        );
      } catch (err) {
        if (!isMounted) return;

        console.error(
          "FAILED TO LOAD PROJECTS:",
          err
        );

        setError(
          err?.message ||
            "Failed to load projects."
        );
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchProjects();

    return () => {
      isMounted = false;
    };
  }, []);

  // ==========================================================
  // GROUP PROJECTS
  // ==========================================================

  const groupedProjects =
    projects.reduce(
      (groups, project) => {
        const category =
          project.category ||
          "other";

        if (!groups[category]) {
          groups[category] = [];
        }

        groups[category].push(
          project
        );

        return groups;
      },
      {}
    );

  // ==========================================================
  // SORT CATEGORIES
  // ==========================================================

  const categories =
    Object.keys(
      groupedProjects
    ).sort((a, b) => {
      const indexA =
        CATEGORY_ORDER.indexOf(a);

      const indexB =
        CATEGORY_ORDER.indexOf(b);

      if (
        indexA !== -1 &&
        indexB !== -1
      ) {
        return indexA - indexB;
      }

      if (indexA !== -1) {
        return -1;
      }

      if (indexB !== -1) {
        return 1;
      }

      return a.localeCompare(b);
    });

  // ==========================================================
  // MODAL
  // ==========================================================

  const openProjectModal = (
    project
  ) => {
    setSelectedProject(project);
    setImageIndex(0);

    document.body.style.overflow =
      "hidden";
  };

  const closeProjectModal = () => {
    setSelectedProject(null);
    setImageIndex(0);

    document.body.style.overflow =
      "";
  };

  // ==========================================================
  // PREVIOUS IMAGE
  // ==========================================================

  const prevImage = () => {
    if (
      !selectedProject?.images?.length
    ) {
      return;
    }

    setImageIndex((prev) =>
      prev === 0
        ? selectedProject.images
            .length - 1
        : prev - 1
    );
  };

  // ==========================================================
  // NEXT IMAGE
  // ==========================================================

  const nextImage = () => {
    if (
      !selectedProject?.images?.length
    ) {
      return;
    }

    setImageIndex((prev) =>
      prev ===
      selectedProject.images.length -
        1
        ? 0
        : prev + 1
    );
  };

  // ==========================================================
  // KEYBOARD CONTROLS
  // ==========================================================

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedProject) {
        return;
      }

      if (e.key === "Escape") {
        closeProjectModal();
      }

      if (e.key === "ArrowLeft") {
        prevImage();
      }

      if (e.key === "ArrowRight") {
        nextImage();
      }
    };

    window.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      window.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [selectedProject]);

  // ==========================================================
  // PROJECT CARD
  // ==========================================================

  const VisualProjectCard = ({
    project,
    projectNumber,
  }) => {
    const title =
      getProjectName(project);

    const description =
      getProjectDescription(
        project
      );

    const images =
      project.images || [];

    const category =
      getCategoryTitle(
        project.category
      );

    return (
      <article
        className="project-card enhanced-card"
        onClick={() =>
          openProjectModal(
            project
          )
        }
      >
        {/* IMAGE SHOWCASE */}

        <div className="card-image-wrapper">
          {images.length > 0 ? (
            <div className="project-visual">
              <img
                src={images[0]}
                alt={title}
                loading="lazy"
                className="project-main-image"
              />

              <div className="image-gradient" />

              {images.length > 1 && (
                <div className="mini-preview-strip">
                  {images
                    .slice(1, 3)
                    .map(
                      (
                        image,
                        index
                      ) => (
                        <div
                          className="mini-preview"
                          key={index}
                        >
                          <img
                            src={image}
                            alt=""
                            loading="lazy"
                          />
                        </div>
                      )
                    )}
                </div>
              )}

              {images.length > 3 && (
                <span className="more-images">
                  +{images.length - 3}
                </span>
              )}

              <div className="project-number">
                {String(
                  projectNumber
                ).padStart(2, "0")}
              </div>

              <div className="card-view-indicator">
                <FaPlus />
              </div>
            </div>
          ) : (
            <div className="no-project-image">
              <span>EMKA</span>
              <small>
                {t("No images")}
              </small>
            </div>
          )}
        </div>

        {/* CONTENT */}

        <div className="card-content">
          <div className="card-topline">
            <span className="project-category">
              {category}
            </span>

            <span className="project-image-count">
              {images.length}{" "}
              {t("images")}
            </span>
          </div>

          <h3>{title}</h3>

          {description && (
            <p className="card-description">
              {description}
            </p>
          )}

          <div className="card-footer">
            <span className="view-project">
              {t("View Project")}
            </span>

            <span className="project-arrow">
              ↗
            </span>
          </div>
        </div>
      </article>
    );
  };

  // ==========================================================
  // SECTION
  // ==========================================================

  const Section = ({
    category,
    projects = [],
  }) => {
    if (
      !projects ||
      projects.length === 0
    ) {
      return null;
    }

    return (
      <section className="section-wrapper reveal">
        <div className="section-heading">
          <div>
            <span className="section-index">
              // 0
              {CATEGORY_ORDER.indexOf(
                category
              ) + 1}
            </span>

            <h2 className="section-title">
              {getCategoryTitle(
                category
              )}
            </h2>
          </div>

          <span className="section-count">
            {String(
              projects.length
            ).padStart(2, "0")}{" "}
            {t("PROJECTS")}
          </span>
        </div>

        <div className="projects-section">
          {projects.map(
            (
              project,
              index
            ) => (
              <VisualProjectCard
                key={
                  project.id
                }
                project={
                  project
                }
                projectNumber={
                  index + 1
                }
              />
            )
          )}
        </div>
      </section>
    );
  };

  // ==========================================================
  // LOADING
  // ==========================================================

  if (loading) {
    return (
      <div
        className="projectspage-container"
        ref={containerRef}
        onMouseMove={
          handleMouseMove
        }
      >
        <div
          className="ambient-glow"
          aria-hidden="true"
        />

        <div className="projectspage-main">
          <header className="projects-header reveal">
            <span className="eyebrow">
              // EMKA — portfolio
            </span>

            <h1 className="projects-main-title">
              {t("Our Projects")}
            </h1>

            <p className="projects-intro">
              {t(
                "A selection of what we build."
              )}
            </p>
          </header>

          <div className="projects-loading">
            <span className="loading-dot" />
            {t(
              "Loading projects..."
            )}
          </div>
        </div>
      </div>
    );
  }

  // ==========================================================
  // ERROR
  // ==========================================================

  if (error) {
    return (
      <div
        className="projectspage-container"
        ref={containerRef}
        onMouseMove={
          handleMouseMove
        }
      >
        <div
          className="ambient-glow"I
          aria-hidden="true"
        />

        <div className="projectspage-main">
          <header className="projects-header reveal">
            <span className="eyebrow">
              // EMKA — portfolio
            </span>

            <h1 className="projects-main-title">
              {t("Our Projects")}
            </h1>
          </header>

          <div className="projects-error">
            {t(
              "Failed to load projects."
            )}
          </div>
        </div>
      </div>
    );
  }

  // ==========================================================
  // MAIN RENDER
  // ==========================================================

  return (
    <div
      className="projectspage-container"
      ref={containerRef}
      onMouseMove={
        handleMouseMove
      }
    >
      <div
        className="ambient-glow"
        aria-hidden="true"
      />

      <div className="projectspage-main">

        {/* HEADER */}

        <header className="projects-header reveal">
          <span className="eyebrow">
            // EMKA — portfolio
          </span>

          <h1 className="projects-main-title">
            {t("Our Projects")}
          </h1>

          <p className="projects-intro">
            {t(
              "A selection of what we build."
            )}
          </p>

          <div className="portfolio-status">
            <span className="status-pulse" />
            <span>
              {t(
                "SYSTEMS BUILT WITH PURPOSE"
              )}
            </span>
          </div>
        </header>

        {/* PROJECT SECTIONS */}

        {categories.map(
          (category) => (
            <Section
              key={category}
              category={category}
              projects={
                groupedProjects[
                  category
                ]
              }
            />
          )
        )}

        {projects.length ===
          0 && (
          <div className="projects-empty">
            {t(
              "No projects available."
            )}
          </div>
        )}

        {/* SOCIAL */}

        <div className="bottom-social-row reveal">
          <a
            href="https://www.facebook.com/EmkaCode.tech"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook"
          >
            <FaFacebookF />
          </a>

          <a
            href="https://www.instagram.com/emkacode/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
          >
            <FaInstagram />
          </a>
        </div>
      </div>

      {/* ======================================================
          PROJECT MODAL
      ====================================================== */}

      {selectedProject && (
        <div
          className="modal-overlay"
          onClick={
            closeProjectModal
          }
        >
          <div
            className="modal-content enhanced-modal"
            onClick={(e) =>
              e.stopPropagation()
            }
          >
            {/* CLOSE */}

            <button
              className="close-btn"
              onClick={
                closeProjectModal
              }
              aria-label={t(
                "Close"
              )}
            >
              <FaTimes />
            </button>

            {/* HEADER */}

            <div className="modal-header">
              <div className="modal-heading">
                <span className="modal-eyebrow">
                  //{" "}
                  {getCategoryTitle(
                    selectedProject.category
                  )}
                </span>

                <h2>
                  {getProjectName(
                    selectedProject
                  )}
                </h2>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="modal-language-switcher"
                  onClick={() =>
                    changeLanguage(
                      i18n.language ===
                        "ar"
                        ? "en"
                        : "ar"
                    )
                  }
                  aria-label={t(
                    "Change language"
                  )}
                >
                  <FaGlobe />

                  <span>
                    {i18n.language ===
                    "ar"
                      ? "EN"
                      : "AR"}
                  </span>
                </button>
              </div>
            </div>

            {/* BODY */}

            <div className="modal-body">

              {/* IMAGE SHOWCASE */}

              {selectedProject.images &&
              selectedProject.images.length >
                0 ? (
                <div className="modal-image-section">

                  <div className="modal-image-container">

                    <div className="modal-image-background" />

                    <button
                      className="nav-btn left"
                      onClick={
                        prevImage
                      }
                      aria-label={t(
                        "Previous image"
                      )}
                    >
                      <FaChevronLeft />
                    </button>

                    <div className="modal-image-wrapper">
                      <img
                        src={
                          selectedProject
                            .images[
                            imageIndex
                          ]
                        }
                        alt={`${getProjectName(
                          selectedProject
                        )} ${
                          imageIndex +
                          1
                        }`}
                        className="modal-image"
                      />
                    </div>

                    <button
                      className="nav-btn right"
                      onClick={
                        nextImage
                      }
                      aria-label={t(
                        "Next image"
                      )}
                    >
                      <FaChevronRight />
                    </button>

                    <div className="modal-image-counter">
                      <span>
                        {String(
                          imageIndex +
                            1
                        ).padStart(
                          2,
                          "0"
                        )}
                      </span>

                      <i>/</i>

                      <span>
                        {String(
                          selectedProject
                            .images
                            .length
                        ).padStart(
                          2,
                          "0"
                        )}
                      </span>
                    </div>
                  </div>

                  {/* THUMBNAILS */}

                  <div className="thumbnail-gallery">
                    {selectedProject.images.map(
                      (
                        img,
                        idx
                      ) => (
                        <button
                          type="button"
                          key={idx}
                          className={`thumbnail ${
                            idx ===
                            imageIndex
                              ? "active"
                              : ""
                          }`}
                          onClick={() =>
                            setImageIndex(
                              idx
                            )
                          }
                        >
                          <img
                            src={img}
                            alt=""
                            loading="lazy"
                          />

                          <span>
                            {String(
                              idx + 1
                            ).padStart(
                              2,
                              "0"
                            )}
                          </span>
                        </button>
                      )
                    )}
                  </div>
                </div>
              ) : (
                <div className="no-modal-image">
                  <span>
                    EMKA
                  </span>

                  <small>
                    {t(
                      "No images available."
                    )}
                  </small>
                </div>
              )}

              {/* DESCRIPTION */}

              {getProjectDescription(
                selectedProject
              ) && (
                <div className="modal-description">
                  <span className="description-label">
                    //{" "}
                    {t(
                      "About this project"
                    )}
                  </span>

                  <p>
                    {getProjectDescription(
                      selectedProject
                    )}
                  </p>
                </div>
              )}

              {/* APP DOWNLOADS */}

              {selectedProject.category ===
                "app" &&
                (selectedProject.apple_link?.trim() ||
                  selectedProject.google_link?.trim()) && (
                  <div className="app-download-section">
                    <div className="download-heading">
                      <div>
                        <span className="description-label">
                          //{" "}
                          {t(
                            "Available on"
                          )}
                        </span>

                        <h4>
                          {t(
                            "Download the app"
                          )}
                        </h4>
                      </div>
                    </div>

                    <div className="app-download-buttons">
                      {selectedProject.apple_link?.trim() && (
                        <a
                          className="app-download-btn"
                          href={selectedProject.apple_link.trim()}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <FaApple />

                          <span>
                            {t(
                              "App Store"
                            )}
                          </span>

                          <FaExternalLinkAlt className="external-icon" />
                        </a>
                      )}

                      {selectedProject.google_link?.trim() && (
                        <a
                          className="app-download-btn"
                          href={selectedProject.google_link.trim()}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <FaGooglePlay />

                          <span>
                            {t(
                              "Google Play"
                            )}
                          </span>

                          <FaExternalLinkAlt  className="external-icon" />
                        </a>
                      )}
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectsPage;

